/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import {
  type RequiredVehicleCatalogListingColor,
  type RequiredNullableRangeNumber,
} from '@/page/catalog-vehicles/store/catalog-filter.store';

/**
 * Formats numbers to the nearest thousand with the suffix of K
 *
 * @example 100000 -> 100K
 */
export function formatThousand(value: number, options: Intl.NumberFormatOptions = {}): string {
  const opts: Intl.NumberFormatOptions = {
    ...options,
    minimumSignificantDigits: 2,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  return (value < 1000 && value.toString()) || `${(value / 1000).toLocaleString('en-US', opts)}K`;
}

type ReadonlyStringArray = readonly string[];

export type EncoderFn = (values: ReadonlyStringArray) => ReadonlyStringArray;

export function concatReadonlyArrays(
  target: ReadonlyStringArray,
  slice: ReadonlyStringArray | string
): ReadonlyStringArray {
  return (typeof slice === 'string' && [...target, slice]) || target.concat(slice);
}

type MapFn<T> = (obj: T) => string;

export function encodeMappedFilter<T extends Readonly<object>>(
  filters: readonly T[],
  mapFn: MapFn<T>
): EncoderFn {
  return (values) =>
    (filters.length === 0 && values) || concatReadonlyArrays(values, filters.map(mapFn));
}

export function encodeArrayFilter(filter: ReadonlyStringArray): EncoderFn {
  return (values) => (filter.length === 0 && values) || concatReadonlyArrays(values, filter);
}

export function encodeRangeNumber(
  range: RequiredNullableRangeNumber,
  format: (value: number) => string = String,
  defaultRange: RequiredNullableRangeNumber = { min: null, max: null },
  suffix?: string
): EncoderFn {
  return (values) => {
    if (
      (range.max == null && range.min == null) ||
      (range.min === defaultRange.min && range.max === defaultRange.max)
    ) {
      return values;
    }

    const encodedValue = Array.from(
      new Set([range.min, range.max].filter((value) => value != null).map(format))
    ).join('-');

    return concatReadonlyArrays(values, `${encodedValue}${suffix != null ? ` ${suffix}` : ''}`);
  };
}

export function encodeColor(filter: readonly RequiredVehicleCatalogListingColor[]): EncoderFn {
  return encodeMappedFilter<RequiredVehicleCatalogListingColor>(filter, (color) => color.name);
}

export function reduceFiltersTo(start: ReadonlyStringArray): {
  pipe(...fns: EncoderFn[]): ReadonlyStringArray;
} {
  let values = start;

  return {
    pipe(...fns: EncoderFn[]) {
      for (const fn of fns) {
        values = fn(values);
      }

      return values;
    },
  };
}
